import * as _ from "lodash";

import { AbstractUploadProperty } from "./AbstractUploadProperty";
import * as Textures from "oc-threejs/Textures";
import { UniversalLoader } from "oc-threejs/loader/UniversalLoader";
import { _toThreeComplex } from "./AbstractProperty";
import { MaterialJson } from "../metadata/MaterialJson";
import {
  GetMaterialUvTransform,
  GetMaterialUvTransformMap,
} from "oc-threejs/materials/GetMaterialUvTransform";
import { Material, SRGBColorSpace, Texture } from "three";
import * as THREE from "three";

export class PTexture extends AbstractUploadProperty<MaterialJson> {
  constructor(args) {
    super({
      type: "Texture",
      ...args,
    });
  }

  typeCheck(value) {
    return _.isString(value);
  }

  fromThree(instance: any) {
    const threeValue: Texture = instance?.[this.getNameThree()];
    if (threeValue == null) {
      this.set(null);
      return;
    }
    const sourceFile = (threeValue as any).sourceFile;
    if (sourceFile == null) {
      const path = this.contentPath.ensureRight("/");
      const fileName = this.node.getName() + "_" + this.name + ".png";
      const img = threeValue.image;
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext("2d").drawImage(img, 0, 0);
      this.uploadPendingCounter++;
      canvas.toBlob((blob) => {
        this.upload({
          fileName,
          blob,
        })
          .then((url) => {
            (threeValue as any).sourceFile = url;
            this.set(this.toAbsoluteUrl(url));
            this.uploadPendingCounter--;
          })
          .catch((message) => {
            this.uploadPendingCounter--;
          });
      }, "image/png");
    } else {
      this.set(this.toAbsoluteUrl(sourceFile));
    }
    const textureUsedForUvTransform = GetMaterialUvTransformMap(this.node.threeValue);
    if (textureUsedForUvTransform == null) {
      throw new Error("texture is null what should not happen => error in GetMaterialUvTransform");
    }
    if (textureUsedForUvTransform?.matrixAutoUpdate) {
      textureUsedForUvTransform.updateMatrix();
    }
    this.setUvTransformParameters(textureUsedForUvTransform);
  }

  setUvTransformParameters(texture) {
    const findWrapping = (wrap) =>
      ["ClampToEdgeWrapping", "RepeatWrapping", "MirroredRepeatWrapping"].find(
        (k) => wrap == THREE[k],
      );
    this.node.WrapS = findWrapping(texture.wrapS);
    this.node.WrapT = findWrapping(texture.wrapT);
    this.node.Rotation = texture.rotation;
    this.node.Repeat = texture.repeat.toArray();
    this.node.Center = texture.center.toArray();
    this.node.Offset = texture.offset.toArray();
    this.node.FlipY = texture.flipY;
  }

  threeToInternalFormat(value: Texture): string {
    const sourceFile = (value as any)?.sourceFile;
    return sourceFile == null ? null : this.toAbsoluteUrl(sourceFile);
  }

  toThree(instance: any) {
    return _toThreeComplex<string, Material, Texture, MaterialJson>(
      this,
      instance,
      "Texture",
      (instance, value) => {
        let threeValue;
        const promise = new Promise((resolve: (value: Texture) => void, reject) => {
          threeValue = UniversalLoader.loadTexture(value, undefined, resolve, reject);
        });
        return { promise, threeValue };
      },
      (instance, value, texture, freshlyCreated) => {
        const update = () => {
          texture.anisotropy = Textures.getMaxAnisotropy();

          // https://threejs.org/docs/index.html#examples/en/loaders/GLTFLoader
          // Textures containing color information (.map, .emissiveMap, and .specularMap) always use sRGB colorspace in glTF, while vertex colors and material properties
          // (.color, .emissive, .specular) use linear colorspace.
          // In a typical rendering workflow, textures are converted to linear colorspace by the renderer, lighting calculations are made,
          // then final output is converted back to sRGB and displayed on screen. Unless you need post-processing in linear colorspace,
          // always configure WebGLRenderer as follows when using glTF.
          // GLTFLoader will automatically configure textures referenced from a .gltf or .glb file correctly, with the assumption that the renderer is set up as shown above.
          // When loading textures externally (e.g., using TextureLoader) and applying them to a glTF model, colorspace and orientation must be given:
          if (/^(map|emissiveMap|specularMap)$/g.test(this.getNameThree())) {
            texture.colorSpace = SRGBColorSpace;
          }
          if (this.node.FlipY != null) {
            // https://github.com/mrdoob/three.js/issues/5644
            // FlipY ist default true für Threejs: "I think it has to do with most image formats having 0,0 as the top left corner of the image and WebGL
            // wanting 0,0 to be in the bottom left corner."
            // https://threejs.org/docs/index.html#examples/en/loaders/GLTFLoader
            // Aber für GLTF muss flipY false sein: UVs use the convention that (0, 0) corresponds to the upper left corner of a texture.
            texture.flipY = this.node.FlipY;
          }

          if (this.node.WrapS) {
            texture.wrapS = THREE[this.node.WrapS];
          }
          if (this.node.WrapT) {
            texture.wrapT = THREE[this.node.WrapT];
          }
          if (Number.isFinite(this.node.Rotation)) {
            texture.rotation = this.node.Rotation;
          }
          if (this.node.Repeat) {
            texture.repeat.fromArray(this.node.Repeat);
          }
          if (this.node.Center) {
            texture.center.fromArray(this.node.Center);
          }
          if (this.node.Offset) {
            texture.offset.fromArray(this.node.Offset);
          }
          texture.needsUpdate = true;
          this.node.threeValue.needsUpdate = true;
        };
        if (texture.image) {
          if (freshlyCreated || texture.image.src == value) {
            update();
          } else {
            (texture as any).sourceFile = value;
            const img = new Image();
            img.src = value;
            texture.image
              .decode()
              .then(() => {
                texture.image = img;
                update();
              })
              .catch((error) => {
                console.error("image could not be loaded:", value, error);
              });
          }
        }
      },
      true,
    );
  }
}
